import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../lib/firebase';
import { collection, addDoc, serverTimestamp, doc, updateDoc } from 'firebase/firestore';
import { Address } from '../types';
import { notify } from '../lib/toast';

interface AddressFormProps {
    onSuccess: () => void;
    onCancel: () => void;
    initialData?: Address;
}

export default function AddressForm({ onSuccess, onCancel, initialData }: AddressFormProps) {
    const { currentUser } = useAuth();
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState<Partial<Address>>(initialData || {
        title: '',
        fullName: currentUser?.name || '',
        phone: '',
        city: '',
        district: '',
        neighborhood: '',
        street: '',
        buildingNo: '',
        apartmentNo: '',
        zipCode: '',
        isDefault: false
    });

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!currentUser) return;

        // Validation
        if (!formData.title || !formData.fullName || !formData.phone || !formData.city || !formData.district || !formData.neighborhood || !formData.street || !formData.buildingNo || !formData.apartmentNo) {
            notify.error('Lütfen tüm zorunlu alanları doldurun.');
            return;
        }

        if (!formData.phone?.match(/^5\d{9}$/)) {
            notify.error('Telefon numarası 5xxxxxxxxx formatında olmalıdır.');
            return;
        }

        setLoading(true);

        try {
            const addressData = {
                ...formData,
                createdAt: initialData?.createdAt || Date.now() // Use number based timestamp for client consistency
            };

            if (initialData?.id) {
                // Update existing
                const ref = doc(db, 'users', currentUser.id, 'addresses', initialData.id);
                await updateDoc(ref, addressData);
                notify.success('Adres güncellendi.');
            } else {
                // Create new
                const ref = collection(db, 'users', currentUser.id, 'addresses');
                await addDoc(ref, addressData);
                notify.success('Adres eklendi.');
            }
            onSuccess();
        } catch (error) {
            console.error('Adres kaydetme hatası:', error);
            notify.error('Adres kaydedilemedi.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Adres Başlığı */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-400 mb-1">Adres Başlığı (Örn: Evim) *</label>
                    <input
                        type="text"
                        name="title"
                        value={formData.title}
                        onChange={handleChange}
                        className="w-full p-2 border dark:border-white/10 rounded-lg focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        placeholder="Ev, İş, Yazlık vb."
                        required
                    />
                </div>

                {/* Ad Soyad */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-400 mb-1">Teslim Alacak Kişi *</label>
                    <input
                        type="text"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleChange}
                        className="w-full p-2 border dark:border-white/10 rounded-lg focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        placeholder="Ad Soyad"
                        required
                    />
                </div>

                {/* Telefon */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-400 mb-1">Telefon (5xxxxxxxxx) *</label>
                    <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        maxLength={10}
                        className="w-full p-2 border dark:border-white/10 rounded-lg focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        placeholder="5xxxxxxxxx"
                        required
                    />
                </div>

                {/* İl */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-400 mb-1">İl *</label>
                    <input
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        className="w-full p-2 border dark:border-white/10 rounded-lg focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        placeholder="Örn: İstanbul"
                        required
                    />
                </div>

                {/* İlçe */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-400 mb-1">İlçe *</label>
                    <input
                        type="text"
                        name="district"
                        value={formData.district}
                        onChange={handleChange}
                        className="w-full p-2 border dark:border-white/10 rounded-lg focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        placeholder="Örn: Kadıköy"
                        required
                    />
                </div>

                {/* Mahalle */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-400 mb-1">Mahalle *</label>
                    <input
                        type="text"
                        name="neighborhood"
                        value={formData.neighborhood}
                        onChange={handleChange}
                        className="w-full p-2 border dark:border-white/10 rounded-lg focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        placeholder="Örn: Caferağa Mah."
                        required
                    />
                </div>
            </div>

            {/* Sokak / Cadde */}
            <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-400 mb-1">Sokak / Cadde *</label>
                <input
                    type="text"
                    name="street"
                    value={formData.street}
                    onChange={handleChange}
                    className="w-full p-2 border dark:border-white/10 rounded-lg focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                    placeholder="Örn: Moda Caddesi"
                    required
                />
            </div>

            <div className="grid grid-cols-3 gap-4">
                {/* Bina No */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-400 mb-1">Bina No *</label>
                    <input
                        type="text"
                        name="buildingNo"
                        value={formData.buildingNo}
                        onChange={handleChange}
                        className="w-full p-2 border dark:border-white/10 rounded-lg focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        required
                    />
                </div>

                {/* Kat */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-400 mb-1">Kat</label>
                    <input
                        type="text"
                        name="floor"
                        value={formData.floor || ''}
                        onChange={handleChange}
                        className="w-full p-2 border dark:border-white/10 rounded-lg focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                    />
                </div>

                {/* Daire No */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-400 mb-1">Daire No *</label>
                    <input
                        type="text"
                        name="apartmentNo"
                        value={formData.apartmentNo}
                        onChange={handleChange}
                        className="w-full p-2 border dark:border-white/10 rounded-lg focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        required
                    />
                </div>
            </div>

            {/* Posta Kodu */}
            <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-400 mb-1">Posta Kodu</label>
                <input
                    type="text"
                    name="zipCode"
                    value={formData.zipCode || ''}
                    onChange={handleChange}
                    className="w-full p-2 border dark:border-white/10 rounded-lg focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                    placeholder="34710"
                />
            </div>

            {/* Varsayılan Checkbox */}
            <div className="flex items-center">
                <input
                    type="checkbox"
                    id="isDefault"
                    name="isDefault"
                    checked={formData.isDefault}
                    onChange={handleChange}
                    className="h-4 w-4 text-black border-gray-300 rounded focus:ring-black dark:bg-gray-800 dark:border-white/10"
                />
                <label htmlFor="isDefault" className="ml-2 block text-sm text-gray-900 dark:text-gray-400">
                    Varsayılan adres olarak ayarla
                </label>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t dark:border-white/5">
                <button
                    type="button"
                    onClick={onCancel}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-400 bg-white dark:bg-transparent border border-gray-300 dark:border-white/10 rounded-lg hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
                    disabled={loading}
                >
                    İptal
                </button>
                <button
                    type="submit"
                    disabled={loading}
                    className="px-4 py-2 text-sm font-medium text-white dark:text-gray-950 bg-black dark:bg-white rounded-lg hover:bg-gray-900 dark:hover:bg-gray-100 transition-all active:scale-95"
                >
                    {loading ? 'Kaydediliyor...' : 'Kaydet'}
                </button>
            </div>
        </form>
    );
}
